import { Link, useSearchParams } from 'react-router-dom';
import { ArrowRight, ExternalLink } from 'lucide-react';
import { useSEO, localKeywords } from '@/hooks/useSEO';
import './Brands.css';


const BRANDS = [
  {
    key: 'creavit',
    name: 'Creavit',
    tagline: 'Vitrifiye & Banyo Çözümleri',
    description:
      'Creavit, tasarım ve teknolojiyi bir araya getirerek modern, estetik ve işlevsel banyo ürünleri sunmaktadır. Türkiye\'nin önde gelen vitrifiye markası olarak dünya standartlarında üretim yapmaktadır.',
    accent: '#1a1a2e',
    light: '#e8eaf6',
    stats: [
      { label: 'Ürün', value: '889+' },
      { label: 'Kategori', value: '12' },
      { label: 'Rol', value: 'Bölge Distribütörü' },
    ],
    path: '/urunler',
    logo: '/markalogolar/creavit.svg',
  },
  {
    key: 'qua',
    name: 'QUA Granite',
    tagline: 'Porselen Karo & Granit',
    description:
      'QUA, yüksek teknik özellikler ile estetik tasarımı buluşturarak iç ve dış mekan uygulamalarına özel porselen ve granit karolar sunmaktadır. ISO 9001 sertifikalı üretim tesislerinde üretilmektedir.',
    accent: '#795548',
    light: '#efebe9',
    stats: [
      { label: 'Ürün', value: '430+' },
      { label: 'Format', value: '20x120 – 120x120' },
      { label: 'Rol', value: 'Ana Bayi' },
    ],
    path: '/qua',
    logo: '/markalogolar/qualogo.svg',
  },
  {
    key: 'duratiles',
    name: 'Duratiles Akgün',
    tagline: 'Seramik & Porselen Karo',
    description:
      'Duratiles, Akgün Seramik bünyesinde üretilen yenilikçi seramik ve porselen karolar ile yurt içi ve ihracat pazarlarında güçlü bir konuma sahiptir. Mimarların tercihi olan premium seriler sunar.',
    accent: '#7B5E3A',
    light: '#f5f0eb',
    stats: [
      { label: 'Ürün', value: '2.200+' },
      { label: 'Seri', value: '50+' },
      { label: 'Rol', value: 'Ana Bayi' },
    ],
    path: '/duratiles',
    logo: '/markalogolar/duratiles.svg',
  },
  {
    key: 'vitrafix',
    name: 'Vitra Fix',
    tagline: 'Yapıştırıcı & Yapı Kimyasalları',
    description:
      'Vitra Fix, profesyonel seramik yapıştırıcısı, derz maması ve yapı kimyasalları ile her projeye uzun ömürlü, güvenilir çözümler sunan köklü bir yapı markasıdır.',
    accent: '#2c5f2e',
    light: '#e8f5e9',
    stats: [
      { label: 'Ürün', value: '42+' },
      { label: 'Uygulama', value: 'İç & Dış Mekan' },
      { label: 'Rol', value: 'Ana Bayi' },
    ],
    path: '/vitrafix',
    logo: '/markalogolar/vitrafix.svg',
  },
  {
    key: 'kalekim',
    name: 'Kalekim',
    tagline: 'Yapı Kimyasalları & Çözümleri',
    description:
      'Türkiye\'nin yapı kimyasalları sektöründeki lider markası Kalekim; seramik yapıştırıcıları, derz dolguları ve su yalıtım sistemlerinde dünya standartlarında kalite sunar.',
    accent: '#ED1B24',
    light: '#fde8e9',
    stats: [
      { label: 'Ürün', value: '115+' },
      { label: 'Kategori', value: 'Yapı Kimyasalları' },
      { label: 'Rol', value: 'Ana Bayi' },
    ],
    path: '/kalekim',
    logo: '/markalogolar/kalekim.svg',
  },
];

export default function Brands() {
  useSEO({
    title: 'Markalarımız | Creavit, Duratiles, QUA, Vitrafix, Kalekim — Diyarbakır',
    description:
      "Mayi Mimarlık Diyarbakır: Creavit vitrifiye bölge distribütörü; Duratiles, QUA Granite, Vitra Fix ve Kalekim ana bayi. Tüm markalar tek showroom'da — Kayapınar.",
    keywords: localKeywords(
      'diyarbakır seramik markaları',
      'creavit vitrifiye diyarbakır',
      'duratiles bayi',
      'qua granite bayi',
      'kalekim bayi diyarbakır'
    ),
    canonical: 'https://mayimimarlik.com/markalar',
  });
  const [params] = useSearchParams();
  const highlight = params.get('brand');

  return (
    <div className="brands-page" style={{ paddingTop: 'calc(var(--header-height) + var(--spacing-3xl))', minHeight: '100vh' }}>
      <div className="container">
        {/* Header */}
        <div className="brands-page-header">
          <span className="text-accent text-sm font-semibold uppercase tracking-widest mb-sm block">
            Partner Portföyümüz
          </span>
          <h1 className="section-title mb-sm">Temsil Ettiğimiz Markalar</h1>
          <div className="section-divider mb-lg" />
          <p className="text-secondary" style={{ maxWidth: '640px', lineHeight: '1.8' }}>
            Güneydoğu Anadolu'da bölge distribütörlüğünü ve ana bayiliğini üstlendiğimiz sektörün
            önde gelen markalarıyla projelerinize değer katıyoruz.
          </p>
        </div>

        {/* Brand Cards */}
        <div className="brand-portfolio-grid">
          {BRANDS.map(brand => (
            <div
              key={brand.key}
              className={`brand-portfolio-card${highlight === brand.key ? ' highlighted' : ''}`}
              style={{ '--brand-accent': brand.accent, '--brand-light': brand.light } as React.CSSProperties}
            >
              {/* Logo Area */}
              <div className="bpc-logo-area">
                <div className="bpc-logo-badge">
                  <img
                    src={brand.logo}
                    alt={brand.name}
                    className="bpc-logo-img"
                  />
                </div>
              </div>

              {/* Description */}
              <p className="bpc-description">{brand.description}</p>

              {/* Stats */}
              <div className="bpc-stats">
                {brand.stats.map(s => (
                  <div key={s.label} className="bpc-stat">
                    <span className="bpc-stat-value">{s.value}</span>
                    <span className="bpc-stat-label">{s.label}</span>
                  </div>
                ))}
              </div>

              {/* CTA */}
              <div className="bpc-footer">
                <Link to={brand.path} className="bpc-btn">
                  Ürünleri İncele <ArrowRight size={16} />
                </Link>
                <a
                  href={`https://www.${brand.key}.com.tr`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bpc-ext-link"
                >
                  <ExternalLink size={14} />
                  Web Sitesi
                </a>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="brands-page-cta glass" style={{ textAlign: 'center', padding: 'var(--spacing-3xl)', borderRadius: 'var(--radius-lg)', marginTop: 'var(--spacing-4xl)' }}>
          <h3 className="text-2xl mb-md">Tüm Ürünlere Göz Atmak İster misiniz?</h3>
          <p className="text-secondary mb-xl">
            3.500'ü aşkın ürünümüzü tek sayfada görüntüleyin.
          </p>
          <Link to="/tum-urunler" className="btn btn-primary">
            Tüm Ürünlerimizi Gör <ArrowRight size={18} />
          </Link>
        </div>
      </div>
    </div>
  );
}
